import { useState } from "react";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import { SelectInput } from "~/components/SelectInput";
import { useUser } from "~/modules/user/hooks/useUser";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";
import { TOKEN_TIMESERIES_GROUP_BY_LABELS } from "~/modules/token_timeseries/consts";
import { SelectOption } from "~/components/SelectInput/types";
import { cn } from "~/lib/utils";
import { useWindowSize } from "~/utils/useWindowSize";

// // // //

export function GroupByControls<
    T extends TokenTimeseriesGroupBy | AssetTimeseriesGroupBy,
>(props: {
    value: T;
    allowedOptions: T[];
    onChange: (updated: T) => void;
    labelOverrides?: Partial<Record<T, string>>;
    optionsWithPingIndicator?: T[]; // Options to call out with a pulsing dot ping indicator
    /**
     * Below the `@2xl` container breakpoint, stack the label above the select
     * and let the control fill its container instead of sitting inline at a
     * fixed width. Requires an `@container` ancestor — TimeseriesCard's control
     * bar provides one.
     */
    stackLabelOnNarrow?: boolean;
}) {
    const {
        value,
        allowedOptions,
        onChange,
        labelOverrides,
        optionsWithPingIndicator,
        stackLabelOnNarrow = false,
    } = props;
    const { isLoggedIn } = useUser();

    const { isSm, isMd } = useWindowSize();
    const showInsetLabel = isSm || isMd;

    // The new-option dots are dismissed once the user interacts with the control
    const [indicatorDismissed, setIndicatorDismissed] = useState(false);

    const showNewOptionIndicator =
        Boolean(optionsWithPingIndicator?.length) && !indicatorDismissed;

    const getLabel = (opt: T): string => {
        if (labelOverrides && labelOverrides[opt]) {
            return labelOverrides[opt] as string;
        }
        return (
            TOKEN_TIMESERIES_GROUP_BY_LABELS[opt as TokenTimeseriesGroupBy] ??
            opt
        );
    };

    const options: SelectOption[] = allowedOptions.map((opt) => ({
        value: opt,
        content: {
            label: getLabel(opt),
            pingIndicator:
                showNewOptionIndicator &&
                optionsWithPingIndicator?.includes(opt),
        },
    }));

    return (
        <div
            className={cn("flex items-center gap-2", {
                "w-full min-w-0 flex-col items-start gap-1 @2xl:w-auto @2xl:flex-row @2xl:items-center @2xl:gap-2":
                    stackLabelOnNarrow,
            })}
        >
            {!showInsetLabel && (
                <p className="text-sm font-semibold text-foreground whitespace-nowrap select-none">
                    Group By
                </p>
            )}
            <div
                className={cn("relative", {
                    "w-full min-w-0 @2xl:w-auto": stackLabelOnNarrow,
                })}
            >
                <SelectLoginGate isLoggedIn={isLoggedIn}>
                    <SelectInput
                        size="sm"
                        value={value}
                        placeholder="Group By"
                        placeholderPosition={
                            showInsetLabel ? "inset" : "hidden"
                        }
                        disabled={!isLoggedIn}
                        className={cn({
                            "@2xl:w-42 w-full min-w-0": stackLabelOnNarrow,
                        })}
                        onChange={(updatedValue) => {
                            setIndicatorDismissed(true);
                            onChange(updatedValue as T);
                        }}
                        options={options}
                    />
                </SelectLoginGate>
                {showNewOptionIndicator && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -top-1 -right-1 flex h-2 w-2"
                    >
                        <span className="absolute inline-flex h-full w-full rounded-full bg-primary animate-ping [animation-duration:3s] motion-reduce:hidden" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                    </span>
                )}
            </div>
        </div>
    );
}
